'use strict'


import {
  forOwn,
  cloneDeep,
  isUndefined,
} from 'lodash'


import {
  schemaSetup,
  normalise,
  validate,
  coerce,
} from './schema'


import {
  storageSetup,
  getOne,
  getAll,
  addOne,
  updateOne,
  deleteOne,
  uploadBlobs,
} from './storage'


const demandJson = (req) => {
  if(! (req.headers['content-type'] || '').match('^application/json;?')) {
    return {
      status: 400,
      errors: [`Content-Type must be application/json`],
    }
  }
}

// export a function that returns a function (because javascript)
export default (opts) => async (ctx) => {

  // --- meta setup

  const req = ctx.req
  if(! req)
    throw `'req' not in context - have you configured function.json?`

  // simplified response object, transformed at the bottom of this function
  let res

  if(! opts.schema)
    throw `no schema specified`
  const schema = opts.schema

  const limit = opts.limit

  // aggregate referenced types
  const schemaReferences = []
  forOwn(schema, (attr, name) => {
    const ref = attr.references
    // dedupe
    if(ref && (schemaReferences.indexOf(ref) === -1))
      schemaReferences.push(attr.references)
  })

  const table = opts.table
  if(! table)
    throw `no table specified`

  if(! opts.name && ! opts.plural)
    throw `no name/plural specified`
  const plural = opts.plural || opts.name
  const singular = opts.singular || plural.substring(0, plural.length - 1)

  const idDelimiter = opts.oneToManyIdDelimiter || ','

  const cors = (() => {
    if(opts.cors) {
      if(opts.cors === true)
        return true
      if(typeof opts.cors !== 'object')
        return [opts.cors]
      else
        return opts.cors
    }
    return null
  })()

  const log = (s) => !opts.silent && ctx.log(s)


  // --- request setup

  log(req)

  // before we extract anything from req, call pre-hook:
  if(opts.pre) {
    const result = opts.pre(ctx)
    let abandon

    // cancel everything else if it returns/resolves truthy
    if(result instanceof Promise)
      abandon = await result
    else
      abandon = result
    if(abandon)
      return
  }

  const id = req.params.id // (optional)

  schemaSetup(schema, idDelimiter, opts.blobContainer)
  // plural is used as partition
  storageSetup(table, plural, schema, limit, opts.storageConnectionString, opts.blobContainer)


  // --- helpers involving ctx

  const getEntity = () =>
    req.body
    ? cloneDeep(req.body)
    : {}

  const notFound = {
    status: 404,
    errors: [`not found`],
  }


  // --- process request

  const verb = req.method

  // override this method?
  if(opts.override && opts.override[verb]) {
    const result = opts.override[verb](ctx)
    if(result instanceof Promise)
      res = await result
    else
      res = result
  }

  else {

    switch(verb) {

      case 'GET':
        // no id specified => get all
        if(isUndefined(id)) {
          const from = String(req.query.from)
          const errors = []

          const order = (() => {
            if(!req.query.order)
              return null
            const o = req.query.order.split(',')
            o.forEach((attr) => {
              // can prepend attr with ! to sort descending
              if(!schema[attr.replace(/^!/, '')])
                errors.push(`can't order by ${attr}`)
            })
            return o
          })()

          const filter = (() => {
            try {
              if(!req.query.filter)
                return null
              return JSON.parse(req.query.filter)
            }
            catch(e) {
              if(e instanceof SyntaxError)
                errors.push(`invalid json for filter`)
              else
                throw e
            }
          })()

          if(errors.length) {
            // don't bother getting any data
            res = { status: 400, errors }
            break
          }

          res = await getAll(from, order, filter)
        }
        else
          res = await getOne(id)

        break


      case 'POST':
        res = demandJson(req)
        if(res)
          break

        // post with id not allowed
        if(! isUndefined(id)) {
          res = notFound
          break
        }

        normalise()
        const entity = getEntity()

        {
          coerce(entity, true)
          const errors = validate(entity)
          if(errors.length) {
            res = { status: 422, errors }
            break
          }
        }

        try {
          await uploadBlobs(entity)
        }
        catch(e) {
          res = e
          break
        }

        res = await addOne(entity)
        break


      case 'PUT':
      case 'PATCH':
        res = demandJson(req)
        if(res)
          break

        // put/patch without id not allowed
        if(isUndefined(id)) {
          res = notFound
          break
        }

        normalise()
        const entityOrPatch = getEntity()

        if(entityOrPatch.id) {
          res = {
            status: 422,
            errors: [`id update not allowed`],
          }
          break
        }

        let originalEntity, updatedEntity
        if(verb === 'PATCH') {
          // get object's current state first
          const getRes = await getOne(id)
          if(getRes.errors) {
            res = getRes
            break
          }

          // we never do partial updates as this could introduce race
          // conditions for schema validation
          originalEntity = getRes.results
          updatedEntity = Object.assign(cloneDeep(originalEntity), entityOrPatch)
        }
        else // verb === 'PUT'
          updatedEntity = entityOrPatch

        delete updatedEntity.id

        {
          coerce(updatedEntity, false)
          const errors = validate(updatedEntity)
          if(errors.length) {
            res = { status: 422, errors }
            break
          }
        }

        try {
          await uploadBlobs(updatedEntity, originalEntity)
        }
        catch(e) {
          res = e
          break
        }

        res = await updateOne(id, updatedEntity)

        break


      case 'DELETE':
        // must provide an id to delete
        if(isUndefined(id)) {
          res = notFound
          break
        }

        // simples.
        res = await deleteOne(id)
        break


      default:
        throw `allowed (but unsupported) http verb invoked`
    }

  }

  // override handlers can explicitly set res to null to prevent aftr's
  // 'post-processing':
  if(res !== null) {

    // default to a blank 200
    if(! res)
      res = {}

    // decide what ctx.res.body should actually hold, and end the request

    if((cors === true) || (
        Array.isArray(cors) && (cors.indexOf(req.headers.origin) !== -1)
    ))
      ctx.res.headers['Access-Control-Allow-Origin'] = '*'

    if(res.errors)
      ctx.res.body = { errors: res.errors }
    else if(res.results)
      ctx.res.body = res.results
    else {
      // send a 'no content'
      ctx.res.status = 204
      ctx.res.body = ''
    }

    if(res.status)
      ctx.res.status = res.status
  }

  // responsibility of calling ctx.done() is passed to post() (if set):
  if(opts.post)
    opts.post(ctx)
  else
    ctx.done()
}


export const _test = {
  demandJson
}



// WEBPACK FOOTER //
// ./src/index.js